<template>
  <div>
    <!-- Payment Gateways List -->
    <div class="payment-gateway-list" v-if="localGatewayIntegrations.length >= 0">
      <div class="gs-gateways-grid">
        <!-- Add Gateway Button -->
        <button type="button" class="gs-gateways-grid__add" @click="openGatewayModal">
          <span class="gs-gateways-grid__add-icon"><gs-icon name="plus" size="22"></gs-icon></span>
          <span>{{ $t('frontend_product_step3_add_payment_gateway') }}</span>
        </button>

        <!-- Display Gateway Assets -->
        <gateway-asset v-for="(gatewayIntegration, gatewayIntegrationIndex) in localGatewayIntegrations"
          :key="gatewayIntegrationIndex" :gatewayIntegration="gatewayIntegration"
          @onProcessingFeeClick="openProcessingFeeModal" @onDeleteGatewayIntegration="openConfirmModal"
          @onEditGatewayIntegration="checkEditGateway" />
      </div>
      <payment-gateway :localGatewayList="localGatewayIntegrations"
        @updateIntegration="localGatewayIntegrations = $event"></payment-gateway>
    </div>
    <!-- No Gateway Message -->
    <div v-else class="gs-gateways-empty">
      {{ $t('frontend_product_step3_add_payment_gateway') }}
    </div>

    <gd-modal name="add-processing-fees-modal" :title="$t('frontend_groovesell_estimated_merchant_fee')">
      <!-- Processing Fee Form -->
      <div class="gs-gateways-modal__body"
        v-if="!['groovepay_automated', 'paypal', 'paypal_commerce'].includes(editingIntegration.platform)">
        <div class="gs-gateways-modal__note">
          <info-icon class="gs-gateways-modal__note-icon" />
          <span>{{ $t('average_merchant_processing_fee') }}</span>
        </div>
        <!-- Percentage Input -->
        <gs-field class="gs-gateways-modal__field"
          :label="$t('frontend_groovesell_per_transaction_rates') + ' (%)'">
          <text-input v-model="editingIntegration.percentage" :attribute="$t('frontend_name')" class="text-dark text-sm"
            :placeholder="$t('frontend_percentage')" />
        </gs-field>
        <!-- Per Transaction Fee Input -->
        <gs-field class="gs-gateways-modal__field"
          :label="$t('frontend_groovesell_per_transaction_fee') + ' ($)'">
          <text-input v-model="editingIntegration.per_transaction_fee" :attribute="$t('frontend_name')"
            class="text-dark text-sm" :placeholder="$t('frontend_groovesell_transaction_fee')" />
        </gs-field>
        <!-- Update Button -->
        <div class="gs-gateways-modal__actions">
          <gs-button variant="primary" @click="updateIntegration()">{{ $t('frontend_update')
            }}</gs-button>
        </div>
      </div>
      <!-- PayPal/GroovePay Message -->
      <div class="gs-gateways-modal__body" v-else>
        {{
          editingIntegration.platform.startsWith('paypal')
            ? 'PayPal'
            : whitelabelAppData('pay').title
        }}
        {{ $t('frontend_groovesell_funnels_settings_fee_auto_calc_api') }}
      </div>
    </gd-modal>
    <!-- confirm modal-->
    <confirm-modal name="confirm-modal" :message-content="$t('frontend_bouses_delete_bonus_text')"
      :button-text="$t('frontend_bouses_delete_confirmation_text')" @confirm="deleteIntegration" />
    <!--  alert modal -->
    <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
    <!-- Edit payment gateway title -->
    <gd-modal name="payment-gateway-modal-edit" :title="$t('frontend_groovesell_edit_gateway')">
      <div class="gs-gateways-modal__body">
        <gs-field class="gs-gateways-modal__field" :label="$t('frontend_groovesell_gateway_name')">
          <text-input v-model="selectedIntegrationName" :attribute="$t('frontend_name')" class="text-dark text-sm"
            :placeholder="$t('frontend_groovesell_gateway_name')" />
        </gs-field>
        <div>
          <p class="gs-gateways-modal__hint">
            {{ $t('frontend_groovesell_cannot_edit_gateway_keys') }}
          </p>
        </div>
        <div class="gs-gateways-modal__actions">
          <gs-button variant="primary" @click="UpdateGateway()">{{
            $t('frontend_update')}}</gs-button>
        </div>
      </div>

    </gd-modal>

  </div>
</template>

<style>
.gs-gateways-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 18px;
}

.gs-gateways-grid__add {
  display: flex;
  min-height: 200px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  color: var(--gs-primary);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  background: var(--gs-primary-soft);
  border: 1.5px dashed var(--gs-primary);
  border-radius: var(--gs-radius-lg);
  cursor: pointer;
  transition: background-color 150ms ease;
}

.gs-gateways-grid__add:hover {
  background: #ffe4f1;
}

.gs-gateways-grid__add-icon {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
  border-radius: 999px;
}

.gs-gateways-empty {
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

/* Modal content: the gd-modal body has no padding of its own. */
.gs-gateways-modal__body {
  padding: 24px 28px 32px;
  color: var(--gs-foreground);
  font-size: 14px;
}

.gs-gateways-modal__note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

.gs-gateways-modal__note-icon {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  fill: currentColor;
}

.gs-gateways-modal__field {
  margin-bottom: 18px;
}

.gs-gateways-modal__hint {
  margin: 0 0 18px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-gateways-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Token-align the gd-modal chrome used by this tab (CSS only, behaviour untouched). */
.gd-modal.add-processing-fees-modal .gd-modal__content > div:first-child,
.gd-modal.payment-gateway-modal-edit .gd-modal__content > div:first-child {
  overflow: hidden;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-lg);
}

.gd-modal.add-processing-fees-modal .gd-modal__content > div:first-child > div:first-child,
.gd-modal.payment-gateway-modal-edit .gd-modal__content > div:first-child > div:first-child {
  height: auto;
  padding: 18px 22px;
  border-bottom: 1px solid var(--gs-border);
  text-transform: none;
}

.gd-modal.add-processing-fees-modal .gd-modal__content > div:first-child > div:first-child > span:first-child,
.gd-modal.payment-gateway-modal-edit .gd-modal__content > div:first-child > div:first-child > span:first-child {
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gd-modal.add-processing-fees-modal .gd-modal__content > div:first-child > div:first-child > span:last-child,
.gd-modal.payment-gateway-modal-edit .gd-modal__content > div:first-child > div:first-child > span:last-child {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--gs-muted-foreground);
  border-radius: var(--gs-radius-md);
  transition: background-color 150ms ease, color 150ms ease;
}

.gd-modal.add-processing-fees-modal .gd-modal__content > div:first-child > div:first-child > span:last-child:hover,
.gd-modal.payment-gateway-modal-edit .gd-modal__content > div:first-child > div:first-child > span:last-child:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}
</style>

<script>
import Integration from '@/models/Integration';
import GatewayAsset from '@/components/GatewayAsset';
import PaymentGateway from '@/components/PaymentGatewayModal';
import InfoIcon from '@/assets/images/icons/InfoIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';
import axios from 'axios';

export default {
  components: {
    PaymentGateway,
    GatewayAsset,
    InfoIcon,
    PlusIcon,
  },
  async created() {
    this.$loader.start('fetchIntegrations');
    this.localGatewayIntegrations = await Integration.$get();
    this.$loader.end('fetchIntegrations');
  },
  data() {
    return {
      selectedIntegrationName: '',
      showEditPayment: false,
      alertProps: {
        show: false,
        variant: 'success',
        title: '',
        message: ''
      },
      formfields: [],
      localGatewayIntegrations: {},
      selectedGatewayId: null,
      editingIntegration: {
        percentage: 0,
        per_transaction_fee: 0,
      },
      editGateway: {},
      selectedIntegrationTitle: '',
    };
  },
  methods: {
    async openProcessingFeeModal(gatewayId) {
      this.$loader.start('openProcessingFeeModal');
      this.editingIntegration = await Integration.$find(gatewayId);
      this.$root.$emit('modal-open', 'add-processing-fees-modal');
      this.$loader.end('openProcessingFeeModal');
    },
    openConfirmModal(gateway_id) {
      this.$root.$emit('modal-open', 'confirm-modal');
      this.selectedGatewayId = gateway_id;
    },
    async checkEditGateway(gatewayId) {
      this.editGateway = await Integration.$find(gatewayId);
      this.selectedIntegrationName = this.editGateway.name;
      this.$root.$emit('modal-open', 'payment-gateway-modal-edit');
    },
    async UpdateGateway() {
      console.log('UpdateGateway', this.editGateway.id, this.formfields, this.selectedIntegrationName);
      const path = window.config.apiCoreUrl + '/integrations/update/' + this.editGateway.id;
      const data = {
        name: this.selectedIntegrationName,
      };
      try {
        this.$loader.start('UpdateGateway');
        const res = await axios.post(path, data);
        this.$loader.end('UpdateGateway');
        this.localGatewayIntegrations = this.localGatewayIntegrations.map((gateway) => {
          if (gateway.id === this.editGateway.id) {
            return {
              ...gateway,
              name: this.selectedIntegrationName,
            };
          }
          return gateway;
        });
        this.alertProps = {
          show: true,
          variant: 'success',
          title: this.$t('frontend_groovesell_update_gateway'),
          message: this.$t('frontend_groovesell_gateway_updated_success'),
        };
        this.$root.$emit('modal-close', 'payment-gateway-modal-edit');
      } catch (e) {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: this.$t('frontend_groovesell_update_gateway'),
          message: this.$t('frontend_groovesell_gateway_updated_error'),
        };
      }
    },



    async deleteIntegration() {
      this.$loader.start('deleteIntegration');
      try {
        const deleteGateway = await Integration.$find(this.selectedGatewayId);
        if (!deleteGateway) {
          return;
        }
        const res = await deleteGateway.delete();
        const status = res?.data?.status || 'error';

        if (status === 'success') {
          this.localGatewayIntegrations = this.localGatewayIntegrations.filter((gateway) => gateway.id !== this.selectedGatewayId);
          this.selectedGatewayId = null;
        }

        this.alertProps = {
          show: true,
          variant: status,
          title: this.$t('frontend_groovesell_delete_gateway'),
          message: res?.data?.message || (status === 'success'
            ? this.$t('frontend_groovesell_gateway_deleted_success')
            : this.$t('frontend_groovesell_gateway_deleted_error')),
        };
      } catch (e) {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: this.$t('frontend_groovesell_delete_gateway'),
          message: e?.response?.data?.message || this.$t('frontend_groovesell_gateway_deleted_error'),
        };
      } finally {
        this.$loader.end('deleteIntegration');
      }
    },
    openGatewayModal() {
      this.$root.$emit('modal-open', 'payment-gateway-modal');
    },
    async updateIntegration() {
      this.$loader.start('updateIntegration');
      await this.editingIntegration.save();
      this.$root.$emit('modal-close', 'add-processing-fees-modal');
      this.$loader.end('updateIntegration');
    },
  },
}
</script>
